/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {SpreaderAccordionProps} from '@ion/dls/src/components/assetdetails/current/SpreaderAccordion';
import {SpreaderInfoCardProps, SpreaderInfoRow} from '@ion/dls/src/components/assetdetails/current/SpreaderInfoCard';

// the per-manufacturer content of the current theme Spreader accordion -
// everything except its title, received time and expanded state ...
type SpreaderLayoutKeys = 'status' | 'statusLabel' | 'tiles' | 'tilesFitContent' | 'granular' | 'liquidTotals' | 'cards';

export type SpreaderLayout = Pick<SpreaderAccordionProps, SpreaderLayoutKeys>;

export type Measurement = {value: string, unit?: string};

// the readings outside a layout's designed slots, labelled and formatted per
// manufacturer ...
export type RemainingRowsParams = {
  readings: GenericObject;
  usedKeys: string[];
  label: (key: string) => string;
  format: (key: string) => unknown;
};

// the temperature readings every manufacturer's Conditions card shows when
// reported ...
export const TEMPERATURE_KEYS = ['roadTemperature', 'airTemperature'];

export const getNoValue = () => translate('common:notAvailable');

/**
 * Splits a formatted spreader reading (e.g. `29.78 mph`, `2,033,344 lbs`)
 * into its value and unit, so the unit can be shown smaller after it.
 * Readings without a trailing unit (`N/A`, `Auto`, `0`) are returned as the
 * value alone.
 *
 * ```typescript
 *
 *   import {splitMeasurement} from './spreaderLayoutShared';
 *   ...
 *
 *   splitMeasurement('500.0001 lbs/mi'); // {value: '500.0001', unit: 'lbs/mi'} ...
 *
 * ```
 *
 * @param   reading The formatted reading.
 * @returns         The reading's `value` and, when it has one, `unit`.
 */
export const splitMeasurement = (reading: unknown): Measurement => {
  const text  = isNil(reading) || reading === '' ? getNoValue() : String(reading).trim();
  const match = text.match(/^(-?[\d.,]+)\s+(\S+)$/);
  return match ? {value: match[1], unit: match[2]} : {value: text};
};

/**
 * Splits a formatted spreader reading like `splitMeasurement`, but only when
 * it is reported - for the layout slots that are omitted rather than shown
 * as `N/A`.
 *
 * ```typescript
 *
 *   import {optionalMeasurement} from './spreaderLayoutShared';
 *   ...
 *
 *   optionalMeasurement('780 gal'); // {value: '780', unit: 'gal'} ...
 *   optionalMeasurement(undefined); // undefined ...
 *
 * ```
 *
 * @param   reading The formatted reading.
 * @returns         The split reading, or `undefined` when not reported.
 */
export const optionalMeasurement = (reading: unknown): Measurement | undefined => (
  !isNil(reading) ? splitMeasurement(reading) : undefined
);

// a labelled card row of a split reading ...
export const toRow = (label: string, reading: unknown): SpreaderInfoRow => ({label, ...splitMeasurement(reading)});

// a card for the given rows - nothing when there are none ...
export const toCard = (title: string, rows: SpreaderInfoRow[]): SpreaderInfoCardProps[] => (
  rows.length ? [{title, rows}] : []
);

// the readings legacy shows that a layout's designed slots don't - so the
// current theme never drops a reading - as an `Other Details` card ...
export const getOtherDetailsCard = (rows: SpreaderInfoRow[]) => (
  toCard(translate('tracking:spreader.current.otherDetails'), rows)
);

// the reported readings outside `usedKeys`, as labelled rows ...
export const getRemainingRows = ({readings, usedKeys, label, format}: RemainingRowsParams) => (
  Object.keys(readings ?? {})
    .filter(function(key) {
      return !usedKeys.includes(key) && !isNil(readings[key]);
    })
    .map(function(key) {
      return toRow(label(key), format(key));
    })
);
